import { useState } from 'react'
import { useWorkerSearch } from '../index.js'
import { Link } from '../../core/index.js'
import { WorkerDetailPanel } from './WorkerDetailPanel.js'
import { WorkerSearchInput, WorkerSearchTable, isSearchable, useDebouncedValue } from './WorkerSearchResults.js'
import { ArrowRight } from 'lucide-react'
import { LoadFailure } from '../../core/index.js'

interface WorkerSearchProps {
  /** How many facilities this person can see - decides the wording, and whether a hit links to its facility. */
  facilityCount: number
}

/**
 * Worker search for the signed-in operator or facility administrator. The
 * API decides what is searched from the session's own token - every
 * facility of the client for an operator, only the managed ones for a
 * facility administrator - so this component passes no scope and cannot
 * widen it.
 */
export function WorkerSearch({ facilityCount }: WorkerSearchProps) {
  const [text, setText] = useState('')
  const query = useDebouncedValue(text)
  const searchQuery = useWorkerSearch(query)
  const hasSeveralFacilities = facilityCount > 1
  const scopeLabel = hasSeveralFacilities ? `your ${facilityCount} facilities` : 'your facility'

  return (
    <div className="max-w-5xl mx-auto px-6 pt-6">
      <WorkerSearchInput
        id="worker-search"
        value={text}
        onChange={setText}
        placeholder={hasSeveralFacilities ? `Search workers by the start of a name or ID (3+ letters), across your ${facilityCount} facilities` : 'Search workers by the start of a name or ID (3+ letters)'}
      />

      {isSearchable(text) && (
        <div className="mt-3">
          {/* Results are shown only once they answer what is typed now - never a previous query's table under new text. */}
          {searchQuery.isError ? (
            <LoadFailure size="xs" message="The search failed." onRetry={() => void searchQuery.refetch()} retrying={searchQuery.isFetching} />
          ) : searchQuery.isPending || searchQuery.data?.query !== text.trim() ? (
            <div className="text-xs text-ink-mid">Searching…</div>
          ) : (
            <WorkerSearchTable
              key={searchQuery.data.query}
              found={searchQuery.data}
              scopeLabel={scopeLabel}
              renderDetail={(hit) => (
                <div>
                  {hasSeveralFacilities && (
                    <div className="px-5 pt-3 bg-canvas border-t border-marine/15">
                      <Link to={`/facilities/${encodeURIComponent(hit.facilityId)}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-marine hover:text-marine-dk">
                        Open {hit.facilityName}
                        <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  )}
                  <WorkerDetailPanel workerId={hit.workerId} />
                </div>
              )}
            />
          )}
        </div>
      )}
    </div>
  )
}
